import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Package, Loader2, AlertCircle } from 'lucide-react';
import { getCheckoutStatus } from '@/lib/api';

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [transaction, setTransaction] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const result = await getCheckoutStatus(sessionId);
        setTransaction(result);

        if (result.payment_status === 'paid') {
          setStatus('success');
          window.dispatchEvent(new Event('cartUpdated'));
        } else if (result.status === 'expired') {
          setStatus('expired');
        } else if (pollCount < 5) {
          setPollCount((prev) => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
      }
    };

    pollPaymentStatus();
  }, [sessionId, pollCount]);

  return (
    <div className="min-h-screen pt-32 pb-24 flex items-center justify-center" data-testid="checkout-success-page">
      <div className="max-w-xl mx-auto px-6 text-center">
        {status === 'loading' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <Loader2 size={48} className="mx-auto text-gold animate-spin" />
            <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
              Processing Your Order...
            </h1>
            <p className="text-white/60">Please wait while we confirm your payment.</p>
          </motion.div>
        )}

        {status === 'success' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-8"
          >
            <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
              <Check size={40} className="text-green-500" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Thank You for Your Order!
              </h1>
              <p className="text-white/60 mb-2">
                Your payment has been processed successfully.
              </p>
              <p className="mono text-sm text-white/40">
                Order ID: {sessionId?.slice(0, 20)}...
              </p>
            </div>

            {transaction?.items && (
              <div className="card-surface p-6 text-left">
                <h2 className="text-lg font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Order Details
                </h2>
                <div className="space-y-3">
                  {transaction.items.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-white/70">
                        {item.title} ({item.format}) x{item.quantity}
                      </span>
                      <span className="mono text-white/50">${item.subtotal?.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t border-white/10 pt-3 flex justify-between font-medium">
                    <span>Total</span>
                    <span className="mono text-gold">${transaction.amount?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Link to="/books" className="btn-primary" data-testid="continue-browsing-btn">
                Continue Browsing
              </Link>
              <Link to="/" className="btn-secondary" data-testid="go-home-btn">
                Return Home
              </Link>
            </div>
          </motion.div>
        )}

        {status === 'pending' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <Package size={48} className="mx-auto text-gold" />
            <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
              Payment Processing
            </h1>
            <p className="text-white/60">
              Your payment is being processed. You will receive a confirmation email shortly.
            </p>
            <Link to="/" className="btn-secondary inline-block">
              Return Home
            </Link>
          </motion.div>
        )}

        {status === 'expired' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <AlertCircle size={48} className="mx-auto text-yellow-500" />
            <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
              Session Expired
            </h1>
            <p className="text-white/60">
              Your checkout session has expired. Please try again.
            </p>
            <Link to="/cart" className="btn-primary inline-block" data-testid="return-to-cart-btn">
              Return to Cart
            </Link>
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <AlertCircle size={48} className="mx-auto text-red-500" />
            <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
              Something Went Wrong
            </h1>
            <p className="text-white/60">
              We couldn't verify your payment. Please contact support if you were charged.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/cart" className="btn-primary">
                Return to Cart
              </Link>
              <Link to="/contact" className="btn-secondary">
                Contact Support
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
